import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { isPreviewActive } from '../lib/previewSession';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Renders a blocking modal when the current user has must_change_password=true.
 * Forces them to change their password before doing anything else.
 * Modal cannot be dismissed — the only ways out are: change password, or log out.
 */
const ForcePasswordChange = () => {
  const { user, logout, checkAuth } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user || !user.must_change_password || user.impersonator) return null;
  // Phase 19C: when the admin is previewing a user that has must_change_password set,
  // we never want to actually change their password — just silence the modal.
  if (isPreviewActive()) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true });
      toast.success('Password updated');
      await checkAuth();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
    setBusy(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* prevent dismiss */ }}>
      <DialogContent
        data-testid="force-pw-dialog"
        className="bg-[#141414] border-amber-700 text-white max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            <ShieldAlert className="w-5 h-5" /> Password change required
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">
            Your administrator reset your password. Choose a new one before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-white text-xs">Current (temporary) password</Label>
            <Input
              data-testid="force-pw-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              className="bg-[#0A0A0A] border-[#262626] text-white h-11"
              required
            />
          </div>
          <div>
            <Label className="text-white text-xs">New password</Label>
            <Input
              data-testid="force-pw-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="At least 8 characters"
              className="bg-[#0A0A0A] border-[#262626] text-white h-11"
              required
            />
          </div>
          <div>
            <Label className="text-white text-xs">Confirm new password</Label>
            <Input
              data-testid="force-pw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white h-11"
              required
            />
          </div>
          {error && (
            <p data-testid="force-pw-error" className="text-red-300 text-xs bg-red-900/30 border border-red-700/50 rounded-md p-2">{error}</p>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" onClick={handleLogout} variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white">
              Log out instead
            </Button>
            <Button data-testid="force-pw-submit" type="submit" disabled={busy} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              <Lock className="w-4 h-4 mr-1" /> {busy ? 'Updating…' : 'Set new password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChange;
